import React, { useState, useEffect } from 'react';
import {
  Wrapper,
  SectionTitle,
  TopRow,
  MetricCard,
  FilterRow,
  FilterLabel,
  ExportButton,
  ToggleView,
  ToggleButton,
  ChartBox,
  ChartWrapper,
  ChartDivider,
  ChartStats,
  StyledBarChart,
  StyledTooltip,
  TableContainer,
  TableHeader,
  TableContent,
} from './SubscribersDashboard.styled';

import { DateRangePicker } from './DateRangePicker.styled';
import GetAppIcon from '@material-ui/icons/GetApp';
import BarChartIcon from '@material-ui/icons/BarChart';
import TableChartIcon from '@material-ui/icons/TableChart';
import {
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import RangeSelect from './RangeSelect';
import moment from 'moment';
import { getNonMemberSignupDashboardData } from '../../../../../redux/services/nonmemberSignUp';

const SubscribersDashboard = ({ selectedOrg }) => {
  const [view, setView] = useState('chart');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rangeOption, setRangeOption] = useState('7'); // Default to "Last 7 days"
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    data: [],
    tableData: [],
    averagePerDay: 0,
  });
  const [exportLoading, setExportLoading] = useState(false);

  const fetchDashboardData = async (start, end, range) => {
    if (!selectedOrg?.id) return;

    setLoading(true);
    try {
      const params = {
        orgId: selectedOrg.id,
        startDate: start ? moment(start).valueOf() : null,
        endDate: end ? moment(end).valueOf() : null,
        range: range || null,
      };

      const result = await getNonMemberSignupDashboardData(params);

      if (!result.error) {
        setDashboardData(result);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRangeChange = e => {
    const value = e.target.value;
    setRangeOption(value);
    setStartDate(null);
    setEndDate(null);
    fetchDashboardData(null, null, value);
  };

  const handlePickerChange = dates => {
    if (dates && dates.length === 2) {
      setStartDate(dates[0]);
      setEndDate(dates[1]);
      setRangeOption('');
      fetchDashboardData(dates[0], dates[1], null);
    }
  };

  // Load initial data on component mount
  useEffect(() => {
    if (selectedOrg?.id) {
      fetchDashboardData(null, null, '7'); // Load with default "Last 7 days"
    }
  }, [selectedOrg?.id]);

  const handleExportCSV = async () => {
    if (!dashboardData.data || dashboardData.data.length === 0) {
      return;
    }

    setExportLoading(true);

    try {
      // Create CSV data with only Date and Signups columns
      const csvData = [['Date', 'Signups']];

      // Add data rows
      dashboardData.data.forEach(item => {
        csvData.push([item.date, item.signups]);
      });

      // Convert to CSV string
      const csvContent = csvData
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');

      // Generate filename based on range option or custom dates
      let dateRangeText = '';
      if (startDate && endDate) {
        dateRangeText = `${moment(startDate).format('MM/DD/YYYY')} - ${moment(
          endDate
        ).format('MM/DD/YYYY')}`;
      } else {
        // Handle predefined range options
        const today = moment();

        switch (rangeOption) {
          case 'today': {
            dateRangeText = `${today.format('MM/DD/YYYY')} - ${today.format(
              'MM/DD/YYYY'
            )}`;
            break;
          }
          case '7': {
            const sevenDaysAgo = moment().subtract(7, 'days');
            dateRangeText = `${sevenDaysAgo.format(
              'MM/DD/YYYY'
            )} - ${today.format('MM/DD/YYYY')}`;
            break;
          }
          case '30': {
            const thirtyDaysAgo = moment().subtract(30, 'days');
            dateRangeText = `${thirtyDaysAgo.format(
              'MM/DD/YYYY'
            )} - ${today.format('MM/DD/YYYY')}`;
            break;
          }
          case 'mtd': {
            const monthStart = moment().startOf('month');
            dateRangeText = `${monthStart.format(
              'MM/DD/YYYY'
            )} - ${today.format('MM/DD/YYYY')}`;
            break;
          }
          case 'ytd': {
            const yearStart = moment().startOf('year');
            dateRangeText = `${yearStart.format('MM/DD/YYYY')} - ${today.format(
              'MM/DD/YYYY'
            )}`;
            break;
          }
          default: {
            const defaultStart = moment().subtract(7, 'days');
            dateRangeText = `${defaultStart.format(
              'MM/DD/YYYY'
            )} - ${today.format('MM/DD/YYYY')}`;
          }
        }
      }

      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 500));

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `Nonmember Signup Form - ${dateRangeText}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Wrapper>
      <SectionTitle>Subscribers</SectionTitle>

      <TopRow>
        <MetricCard>
          <div className="metric-value-label">
            <div className="value">
              {loading ? '...' : dashboardData.totalSignups}
            </div>
            <div className="label">Total Signups</div>
          </div>
        </MetricCard>
        <MetricCard>
          <div className="metric-value-label">
            <div className="value">
              {loading ? '...' : dashboardData.averagePerDay}
            </div>
            <div className="label">Average Per Day</div>
          </div>
        </MetricCard>
      </TopRow>

      <FilterLabel>
        Select a custom time period or time range to view data
      </FilterLabel>

      <FilterRow>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <DateRangePicker
            value={
              startDate && endDate ? [moment(startDate), moment(endDate)] : []
            }
            onChange={handlePickerChange}
            format="MMM DD, YYYY"
            allowClear={true}
            placeholder={['Start Date', 'End Date']}
          />
          <RangeSelect
            value={rangeOption}
            onChange={handleRangeChange}
            options={[
              { label: 'Today', value: 'today' },
              { label: 'Last 7 days', value: '7' },
              { label: 'Last 30 days', value: '30' },
              { label: 'Month to Date', value: 'mtd' },
              { label: 'Year to Date', value: 'ytd' },
            ]}
          />
        </div>

        <ExportButton onClick={handleExportCSV} disabled={exportLoading}>
          <GetAppIcon style={{ fontSize: 14 }} />
          {exportLoading ? 'Exporting...' : 'Export CSV'}
        </ExportButton>
      </FilterRow>

      <ToggleView>
        <ToggleButton
          active={view === 'chart'}
          onClick={() => setView('chart')}
        >
          <BarChartIcon style={{ fontSize: 14 }} />
          Chart View
        </ToggleButton>
        <ToggleButton
          active={view === 'table'}
          onClick={() => setView('table')}
        >
          <TableChartIcon style={{ fontSize: 14 }} />
          Table View
        </ToggleButton>
      </ToggleView>
      <ChartBox style={{ height: '450px' }}>
        {view === 'chart' ? (
          <ChartWrapper>
            <ResponsiveContainer width="100%">
              <StyledBarChart data={dashboardData.data} barSize={50}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={true}
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  axisLine={true}
                  tickLine={true}
                  tick={{ fontSize: 12, fill: '#333' }}
                />
                <YAxis
                  axisLine={true}
                  tickLine={true}
                  tick={{ fontSize: 12, fill: '#333' }}
                />
                <StyledTooltip />
                <Bar dataKey="signups" fill="#2f80ed" radius={[3, 3, 0, 0]} />
              </StyledBarChart>
            </ResponsiveContainer>
            <ChartDivider />
            <ChartStats>
              <div className="value-label">
                <div className="value">
                  {loading ? '...' : dashboardData.totalSignups}
                </div>
                <div className="label">Signups</div>
              </div>
              <div className="value-label">
                <div className="value">
                  {loading ? '...' : dashboardData.averagePerDay}
                </div>
                <div className="label">Average Per Day</div>
              </div>
            </ChartStats>
          </ChartWrapper>
        ) : (
          <TableContainer>
            <TableHeader>
              <div className="left-labels">
                <div className="table-col-header" style={{ width: '100px' }}>
                  Date
                </div>
                <div className="table-col-header" style={{ width: '50px' }}>
                  Signups
                </div>
              </div>
              <div className="table-stats">
                <div className="value-label">
                  <span className="label">Total Signups</span>
                  <span className="value">
                    {loading ? '...' : dashboardData.totalSignups}
                  </span>
                </div>
                <div className="value-label">
                  <span className="label">Average Per Day</span>
                  <span className="value">
                    {loading ? '...' : dashboardData.averagePerDay}
                  </span>
                </div>
              </div>
            </TableHeader>

            <TableContent>
              <table>
                <tbody>
                  {dashboardData.data.map((item, index) => (
                    <tr key={index}>
                      <td style={{ width: '150px' }}>{item.date}</td>
                      <td style={{ width: '50px' }}>{item.signups}</td>
                      <td />
                      <td />
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableContent>
          </TableContainer>
        )}
      </ChartBox>
    </Wrapper>
  );
};

export default SubscribersDashboard;
